import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  FormControlLabel,
  Switch,
  TextField,
  MenuItem,
  Typography,
  Tooltip,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { Close, CloudUpload, Delete, Add } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { useNavigation } from "contexts/notificationsContext";
import { ILinha, IPrenativoLinha, ISalvarLinha, NIVEIS_SISTEMA_LINHA } from "shared/interfaces/linha";
import {
  getLinhaPorId,
  postLinha,
  getPrenativos,
  vincularPrenativoLinha,
  desvincularPrenativoLinha,
} from "services/modules/linhas";

interface ModalLinhaProps {
  isOpen: boolean;
  onClose: () => void;
  refreshData: () => void;
  dataLinha?: ILinha;
}

export const ModalLinha: React.FC<ModalLinhaProps> = ({
  isOpen,
  onClose,
  refreshData,
  dataLinha,
}) => {
  const { openNotification } = useNavigation();
  const [loading, setLoading] = useState(false);

  // Plano 05/10/2026 (Frente 3): a linha em edição vive em estado local derivado da prop. Assim, ao
  // cadastrar uma linha nova, o modal continua aberto já com o id gravado e habilita a seção de
  // prenativos sem depender do pai reabrir o modal.
  const [linhaAtual, setLinhaAtual] = useState<ILinha | undefined>(dataLinha);

  const [descricao, setDescricao] = useState("");
  const [tipoSistemaLinha, setTipoSistemaLinha] = useState("");
  const [idNivelSistema, setIdNivelSistema] = useState<number | "">("");
  // Decisão P3 (02/10/2026, Validação Geral): o controle do pacote N1 volta para a Linha, revogando a
  // opção A de 23/09 (caixa do vínculo). Polaridade positiva, igual à de 17/08: ligado (default) = os
  // cadastros N1 da linha entram no pacote da referência; desligado = pacote separado referência + linha.
  const [gerarNoPacoteReferencia, setGerarNoPacoteReferencia] = useState(true);

  // Logo da linha: os bytes ficam em Linhas.LogoByteArray (mesma coluna que o MOF legado
  // usa). Enviado em base64 e so quando o usuario troca a imagem — salvar sem mexer nela
  // preserva o logo que ja existe.
  const [logoBase64, setLogoBase64] = useState<string>("");
  const [logoAlterada, setLogoAlterada] = useState(false);

  // Prenativos vinculados a linha (aba Prenativo do cadastro legado).
  const [prenativosVinculados, setPrenativosVinculados] = useState<IPrenativoLinha[]>([]);
  const [prenativosDisponiveis, setPrenativosDisponiveis] = useState<IPrenativoLinha[]>([]);
  const [prenativoSelecionado, setPrenativoSelecionado] = useState<number | "">("");

  const [errors, setErrors] = useState<{
    descricao?: string;
    tipoSistemaLinha?: string;
    idNivelSistema?: string;
  }>({});

  const limparFormulario = () => {
    setDescricao("");
    setTipoSistemaLinha("");
    setIdNivelSistema("");
    setGerarNoPacoteReferencia(true);
    setLogoBase64("");
    setLogoAlterada(false);
    setPrenativosVinculados([]);
    setPrenativoSelecionado("");
    setErrors({});
  };

  // Lista de prenativos para o combo (aba Prenativo do cadastro legado).
  useEffect(() => {
    if (!isOpen) return;

    const carregarPrenativos = async () => {
      const lista = await getPrenativos();
      setPrenativosDisponiveis(lista);
    };
    carregarPrenativos();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    setLinhaAtual(dataLinha);

    if (!dataLinha?.idLinha) {
      limparFormulario();
      return;
    }

    const carregar = async () => {
      setLoading(true);
      try {
        const linha = await getLinhaPorId(dataLinha.idLinha);
        const origem = linha ?? dataLinha;
        setDescricao(origem.descricao ?? "");
        setTipoSistemaLinha(origem.tipoSistemaLinha ?? "");
        setIdNivelSistema(origem.idNivelSistema ?? "");
        setGerarNoPacoteReferencia(origem.gerarNoPacoteReferencia ?? true);
        setLogoBase64(origem.logoBase64 ?? "");
        setLogoAlterada(false);
        setPrenativosVinculados(origem.prenativos ?? []);
        setErrors({});
      } finally {
        setLoading(false);
      }
    };
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, dataLinha?.idLinha]);

  const validar = () => {
    const novosErros: typeof errors = {};
    if (!descricao.trim()) novosErros.descricao = "Campo obrigatório";
    if (!tipoSistemaLinha.trim()) novosErros.tipoSistemaLinha = "Campo obrigatório";
    if (!idNivelSistema) novosErros.idNivelSistema = "Campo obrigatório";
    setErrors(novosErros);
    return Object.keys(novosErros).length === 0;
  };

  const handleSelecionarLogo = (evento: React.ChangeEvent<HTMLInputElement>) => {
    const arquivo = evento.target.files?.[0];
    if (!arquivo) return;

    if (arquivo.size > 5 * 1024 * 1024) {
      openNotification({ message: "A imagem deve ter no máximo 5MB.", type: "warning" });
      return;
    }

    const leitor = new FileReader();
    leitor.onload = () => {
      setLogoBase64(String(leitor.result));
      setLogoAlterada(true);
    };
    leitor.readAsDataURL(arquivo);
  };

  const handleRemoverLogo = () => {
    setLogoBase64("");
    setLogoAlterada(true);
  };

  // Prenativos: os vinculos sao gravados na hora pelos endpoints proprios (o cadastro
  // legado funciona assim), entao a linha precisa ja existir.
  const handleVincularPrenativo = async () => {
    if (!prenativoSelecionado || !linhaAtual?.idLinha) return;

    const vinculou = await vincularPrenativoLinha(linhaAtual.idLinha, Number(prenativoSelecionado));
    if (!vinculou) return;

    const prenativo = prenativosDisponiveis.find((p) => p.idPrenativo === prenativoSelecionado);
    if (prenativo) setPrenativosVinculados((atuais) => [...atuais, prenativo]);
    setPrenativoSelecionado("");

    const atualizada = await getLinhaPorId(linhaAtual.idLinha);
    if (atualizada) setPrenativosVinculados(atualizada.prenativos ?? []);
  };

  const handleDesvincularPrenativo = async (idPrenativoLinha?: number) => {
    if (!idPrenativoLinha) return;

    const removeu = await desvincularPrenativoLinha(idPrenativoLinha);
    if (!removeu) return;

    setPrenativosVinculados((atuais) =>
      atuais.filter((p) => p.idPrenativoLinha !== idPrenativoLinha)
    );
  };

  const handleSalvar = async () => {
    if (!validar()) return;

    setLoading(true);
    try {
      const idLinhaAtual = linhaAtual?.idLinha ?? 0;

      // Plano 05/10/2026 (Frente 3): o prenativo escolhido no combo sem clicar em "Adicionar" era
      // descartado em silêncio no Salvar. Agora ele é vinculado antes de gravar a linha.
      if (prenativoSelecionado && idLinhaAtual) {
        await handleVincularPrenativo();
      }

      const payload: ISalvarLinha = {
        idLinha: idLinhaAtual,
        descricao: descricao.trim(),
        tipoSistemaLinha: tipoSistemaLinha.trim(),
        idNivelSistema: Number(idNivelSistema),
        gerarNoPacoteReferencia,
        logoBase64: logoAlterada ? logoBase64 : undefined,
        logoAlterada,
      };

      const idSalvo = await postLinha(payload);
      if (idSalvo === null) return;

      if (idLinhaAtual || !idSalvo) {
        openNotification({
          message: `Linha ${idLinhaAtual ? "atualizada" : "cadastrada"} com sucesso!`,
          type: "success",
        });
        refreshData();
        onClose();
        return;
      }

      // Plano 05/10/2026 (Frente 3): linha NOVA — o modal fica aberto com a linha criada para o
      // usuário vincular os prenativos (os vínculos exigem a linha já gravada).
      const criada = (await getLinhaPorId(idSalvo)) ?? {
        idLinha: idSalvo,
        descricao: payload.descricao,
        tipoSistemaLinha: payload.tipoSistemaLinha,
        idNivelSistema: payload.idNivelSistema,
        gerarNoPacoteReferencia,
        prenativos: [],
      };
      setLinhaAtual(criada);
      setLogoAlterada(false);
      setPrenativosVinculados(criada.prenativos ?? []);
      refreshData();
      openNotification({
        message: "Linha cadastrada. Agora vincule os prenativos e feche.",
        type: "success",
      });
    } catch (error: any) {
      // O serviço lança a mensagem da API (ex.: linha já cadastrada); antes ela não aparecia na tela.
      openNotification({ message: error?.message || "Não foi possível salvar a Linha", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Cadastro de linha
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
          aria-label="Fechar"
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Nome da linha"
              value={descricao}
              onChange={(event) => setDescricao(event.target.value)}
              error={!!errors.descricao}
              helperText={errors.descricao ?? "Obrigatório"}
              inputProps={{ maxLength: 200 }}
            />
          </Grid>

          <Grid item xs={12}>
            <Tooltip title="Desligado, os cadastros de N1 desta linha são gerados em um pacote separado (referência + linha).">
              <FormControlLabel
                control={
                  <Switch
                    checked={gerarNoPacoteReferencia}
                    onChange={(event) => setGerarNoPacoteReferencia(event.target.checked)}
                  />
                }
                label="Gerar no pacote de Referências"
              />
            </Tooltip>
            <Typography variant="caption" display="block" color="text.secondary">
              Desligado, os cadastros de N1 desta linha são gerados em um pacote separado
              (referência + linha).
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Logo da linha
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 90,
                  height: 60,
                  border: "1px dashed",
                  borderColor: "grey.400",
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                {logoBase64 ? (
                  <img src={logoBase64} alt="Logo da linha" style={{ maxWidth: "100%", maxHeight: "100%" }} />
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Sem logo
                  </Typography>
                )}
              </Box>

              <input
                accept="image/*"
                style={{ display: "none" }}
                id="logo-linha-input"
                type="file"
                onChange={handleSelecionarLogo}
              />
              <label htmlFor="logo-linha-input">
                <LoadingButton component="span" variant="outlined" startIcon={<CloudUpload />}>
                  Enviar logo
                </LoadingButton>
              </label>

              {logoBase64 && (
                <Tooltip title="Remover logo">
                  <IconButton color="error" onClick={handleRemoverLogo}>
                    <Delete />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Formatos aceitos: JPG, PNG, GIF, WEBP (máx. 5MB)
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Tipo de Sistema/Linha"
              value={tipoSistemaLinha}
              onChange={(event) => setTipoSistemaLinha(event.target.value)}
              error={!!errors.tipoSistemaLinha}
              helperText={errors.tipoSistemaLinha ?? "Obrigatório"}
              inputProps={{ maxLength: 100 }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Nível do Sistema/Linha"
              value={idNivelSistema}
              onChange={(event) => setIdNivelSistema(Number(event.target.value))}
              error={!!errors.idNivelSistema}
              helperText={errors.idNivelSistema ?? "Obrigatório"}
            >
              {NIVEIS_SISTEMA_LINHA.map((nivel) => (
                <MenuItem key={nivel.value} value={nivel.value}>
                  {nivel.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Prenativos vinculados à linha
            </Typography>

            {!linhaAtual?.idLinha ? (
              <Typography variant="caption" color="text.secondary">
                Salve a linha; depois escolha o prenativo e clique em Adicionar.
              </Typography>
            ) : (
              <>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Prenativo"
                    value={prenativoSelecionado}
                    onChange={(evento) => setPrenativoSelecionado(Number(evento.target.value))}
                  >
                    {prenativosDisponiveis
                      .filter((p) => !prenativosVinculados.some((v) => v.idPrenativo === p.idPrenativo))
                      .map((prenativo) => (
                        <MenuItem key={prenativo.idPrenativo} value={prenativo.idPrenativo}>
                          {prenativo.codigo} — {prenativo.descricao}
                        </MenuItem>
                      ))}
                  </TextField>
                  <LoadingButton
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleVincularPrenativo}
                    disabled={!prenativoSelecionado}
                  >
                    Adicionar
                  </LoadingButton>
                </Box>

                <List dense>
                  {prenativosVinculados.map((prenativo) => (
                    <ListItem
                      key={prenativo.idPrenativoLinha ?? prenativo.idPrenativo}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          color="error"
                          onClick={() => handleDesvincularPrenativo(prenativo.idPrenativoLinha)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={`${prenativo.codigo ?? ""} — ${prenativo.descricao ?? ""}`}
                        secondary={prenativo.unidade}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <Grid container justifyContent="flex-end" spacing={2} sx={{ p: 2 }}>
        <Grid item>
          {/* Plano 05/10/2026 (Frente 3): após criar a linha nova, o botão vira "Fechar" (a linha já está gravada). */}
          <LoadingButton variant="outlined" onClick={onClose} disabled={loading}>
            {linhaAtual?.idLinha && !dataLinha?.idLinha ? "Fechar" : "Cancelar"}
          </LoadingButton>
        </Grid>
        <Grid item>
          <LoadingButton variant="contained" onClick={handleSalvar} loading={loading}>
            Salvar
          </LoadingButton>
        </Grid>
      </Grid>
    </Dialog>
  );
};
